<template>
  <div data-slot="card" class="bg-card text-card-foreground flex flex-col gap-6 rounded-xl border shadow p-4 mb-6 bg-white">
    <div class="flex items-center justify-between mb-4">
      <div>
        <h3 class="font-semibold">{{ referral?.first_name + ' ' + referral?.last_name }}</h3>
        <p class="text-sm text-muted-foreground capitalize">{{ referral?.employerJob?.role_title || 'N/A' }}</p>
        <p class="text-xs text-muted-foreground">Submitted {{ moment(referral?.created_at).format(' Do MMM YYYY') }}</p>
      </div>

      <div class="flex gap-3 flex-nowrap items-center relative">
        <div class="w-[100px]">
          <Popper :hover="true" v-if="referral?.reason">
            <button><img src="@/assets/images/speech-bubble.png" class="w-6 cursor-pointer" alt="" /></button>

            <template #content>
              <div class="rounded-md shadow-md bg-white max-w-[150px] min-w-[100px] p-4">
                <p class="text-[12px] leading-[160%] lowercase">{{ referral?.reason }}</p>
              </div>
            </template>
          </Popper>
        </div>

        <div>
          <div class="mb-1" v-tooltip="'Referral status'">
            <span
              class="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 capitalize"
              >{{ formatString(referral?.refer_status) }}</span
            >
          </div>
          <div v-tooltip="'Application status'">
            <span
              class="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 capitalize"
              >{{ formatString(referral?.job_application_status) }}</span
            >
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center gap-2 overflow-x-auto pb-2">
      <template v-if="stagesCheck.includes(referral?.job_application_status)">
        <template v-for="(status, idx) in stages" :key="status">
          <div class="flex items-center gap-2">
            <div
              class="flex items-center gap-1 px-2 py-1 rounded text-xs whitespace-nowrap capitalize"
              :class="getBg(idx, getIdx(referral?.job_application_status))?.bg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                :class="['lucide lucide-clock h-4 w-4', getBg(idx, getIdx(referral?.job_application_status))?.txt]"
              >
                <template v-if="status == 'pending'">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </template>
                <template v-else-if="status == 'internal_review'">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </template>
                <template v-else-if="status == 'admin_shortlisted'">
                  <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                  <path d="m9 11 3 3L22 4"></path>
                </template>
                <template v-else-if="status == 'client_review'">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </template>
                <template v-else-if="status == 'client_shortlsted'">
                  <path
                    d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"
                  ></path>
                </template>
                <template v-else-if="status == 'hired'">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                  <path d="M4 22h16"></path>
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                </template>
              </svg>
              {{ status == 'pending' ? status + ' ' + 'review' : formatString(status) }}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-chevron-right h-3 w-3 text-gray-400"
            >
              <path d="m9 18 6-6-6-6"></path>
            </svg>
          </div>
        </template>
      </template>

      <template v-else>
        <template v-for="(status, idx) in stages" :key="status">
          <div class="flex items-center gap-2">
            <div
              class="flex items-center gap-1 px-2 py-1 rounded text-xs whitespace-nowrap capitalize"
              :class="getBg(idx, exStages(getIdx(referral?.job_application_status), referral?.job_application_status).length, false)?.bg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                :class="['lucide lucide-clock h-4 w-4', getBg(idx, getIdx(referral?.job_application_status))?.txt]"
              >
                <template v-if="status == 'pending'">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </template>
                <template v-else-if="status == 'internal_review'">
                  <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </template>
                <template v-else-if="status == 'admin_shortlisted'">
                  <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                  <path d="m9 11 3 3L22 4"></path>
                </template>
                <template v-else-if="status == 'client_review'">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </template>
                <template v-else-if="status == 'client_shortlsted'">
                  <path
                    d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z"
                  ></path>
                </template>
                <template v-else-if="status == 'hired'">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                  <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                  <path d="M4 22h16"></path>
                  <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                  <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                  <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                </template>
              </svg>
              {{ status == 'pending' ? status + ' ' + 'review' : formatString(status) }}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-chevron-right h-3 w-3 text-gray-400"
            >
              <path d="m9 18 6-6-6-6"></path>
            </svg>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import moment from 'moment';
import { formatString } from '@/utils/helper';

const props = defineProps({
  referral: {
    type: Object,
  },
});

const stages = ref(['pending', 'internal_review', 'admin_shortlisted', 'client_review', 'client_shortlsted', 'hired']);
const stagesCheck = ref(['pending', 'internal_review', 'admin_shortlisted', 'client_review', 'client_shortlsted', 'client_shortlisted', 'hired']);

const stagesAll = ref([
  'pending',
  'internal_review',
  'admin_shortlisted',
  'admin_rejected',
  'client_review',
  'client_shortlsted',
  'interviewing',
  'assessment_stage',
  'client_rejected',
  'offer_extended',
  'offer_rejected',
  'offer_rescinded',
  'offer_accepted',
  'hired',
  'hold',
]);

const getIdx = (stage) => {
  const stg = stage == 'client_shortlisted' ? 'client_shortlsted' : stage;
  return stages.value.findIndex((item) => item == stg);
};

const getIdxAll = (stage) => {
  return stagesAll.value.findIndex((item) => item === stage);
};

const exStages = (currentStatusIdx, currentStatus = '') => {
  if (currentStatusIdx > -1 || !currentStatus) return [];

  const newIdx = getIdxAll(currentStatus);
  if (newIdx >= 0) {
    const newStages = stagesAll.value.slice(0, newIdx);
    const filteredStages = newStages.filter((item) => stages.value.includes(item));
    console.log(filteredStages, name, 'ooo');
    return filteredStages;
  } else {
    return [];
  }
};

const getBg = (statusIdx, currentStatusIdx, showCurrent = true) => {
  if (Number(statusIdx) == Number(currentStatusIdx)) {
    if (showCurrent) {
      return {
        bg: 'bg-blue-100 text-blue-700',
        txt: 'text-blue-400',
      };
    } else {
      return {
        bg: 'bg-gray-100 text-black',
        txt: 'text-green-400',
      };
    }
  } else if (Number(statusIdx) < Number(currentStatusIdx)) {
    return {
      bg: 'bg-green-100 text-green-700',
      txt: 'text-green-500',
    };
  } else if (Number(statusIdx) > Number(currentStatusIdx)) {
    return {
      bg: 'bg-gray-100 text-black',
      txt: 'text-green-400',
    };
  }
};
</script>

<style scoped>
body {
  background: oklch(0.145 0 0) !important;
}
</style>
